import receiptLogoDataUrl from './eposReceiptLogo.js'

/**
 * 出單機明細單繪製（列印模板 V4）
 * 以 canvas 畫出整張明細單並轉成純黑白，再由 epos.js 以點陣圖送印
 * 改用點陣圖的原因：ePOS 文字指令受限於印表機內建字型與 Big5 字集，
 * 無法完整呈現設計稿（Logo、黑底數量方塊、項目符號等），實機比較後採用點陣圖
 * 出單機：EPSON TM-m10（58mm 紙）
 * 注意：文字由使用者電腦的字型繪製，Mac 與 Windows 呈現會略有差異
 */

export const RECEIPT_CANVAS_WIDTH = 420 // 畫布寬度 dots，等同 Font A 一行 35 個半形字（35 × 12 dots）
export const PRINTER_DPI = 203 // TM-m10 解析度（8 dots/mm），用於換算實體尺寸

/**
 * V4 明細單樣式參數（調整字體大小改這裡）
 *
 * fontScale：字級倍率（建議 0.8 ~ 1.4）
 *   所有文字與欄寬等比例放大，字形不變形。
 *   代價：紙寬固定，每行可放的字數變少，品名與備註更常換行，單據變長。
 *   1 = 內文 24px，約 24 × 24 dots，每行約 17 個中文字。
 *
 * textScaleY：文字垂直拉長倍率（建議 1 ~ 2）
 *   只把文字拉高、不加寬，每行字數不變，但字形會變瘦長。
 *   2 = 與模板 V3 的 printer.addTextSize(1, 2) 相同比例（中文字約 24 × 48 dots）。
 *
 * 調整流程：
 *   1. 到後門頁 /backdoor/eposReceipt 調整這兩個欄位，預覽即為實際送印的點陣圖，可直接列印比較
 *   2. 選定後把頁面上顯示的數值填回下方，正式流程（printThermalPrinter 模板 V4）即全面套用
 */
export const DEFAULT_RECEIPT_STYLE = Object.freeze({
  fontScale: 1.25, // 字級倍率
  textScaleY: 1 // 文字垂直拉長倍率
})

const PADDING = 4 // 左右留白
const BAR_PADDING = 9 // 黑底標題列文字上下留白
const AMOUNT_WIDTH = 76 // 金額欄寬（fontScale = 1 時）
const QTY_BOX_WIDTH = 44 // 數量黑底方塊寬（fontScale = 1 時）
const POINT_COLUMN_WIDTH = 90 // 點數表「交易前」「交易後」欄距（fontScale = 1 時）
const LOGO_WIDTH = 240 // Logo 寬度（不隨樣式參數縮放）
const SIGNATURE_GAP = 64 // 簽名欄與上一欄（時間）之間額外的簽寫空間 dots（約 8mm，8 dots = 1mm）；不隨樣式參數縮放
const BW_THRESHOLD = 160 // 轉黑白的亮度門檻（0~255，越大越黑）
const SPEC_BULLET = '•' // 規格項目符號
const FONT_FAMILY = '"PingFang TC", "Microsoft JhengHei", "Noto Sans TC", sans-serif'

const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

let logoImagePromise = null // Logo 只載入一次，多台出單機共用

const loadLogoImage = () => {
  if (!logoImagePromise) {
    logoImagePromise = new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => {
        logoImagePromise = null
        reject(new Error('明細單 Logo 載入失敗'))
      }
      img.src = receiptLogoDataUrl
    })
  }
  return logoImagePromise
}

/**
 * 產生 Logo 剪影：非透明且非白色的像素一律轉黑
 * 原圖為青綠與深灰雙色，直接轉黑白時青綠色會因亮度過高而消失
 */
const createLogoSilhouette = (img) => {
  const height = Math.round(img.naturalHeight * LOGO_WIDTH / img.naturalWidth)
  const canvas = createCanvas(LOGO_WIDTH, height)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.drawImage(img, 0, 0, LOGO_WIDTH, height)
  const imageData = ctx.getImageData(0, 0, LOGO_WIDTH, height)
  const d = imageData.data
  for (let i = 0; i < d.length; i += 4) {
    const luminance = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
    const value = d[i + 3] >= 128 && luminance < 230 ? 0 : 255
    d[i] = d[i + 1] = d[i + 2] = value
    d[i + 3] = 255
  }
  ctx.putImageData(imageData, 0, 0)
  return canvas
}

const CLOSING_PUNCTUATION = '，。、；：！？）」』》〉' // 不可放在行首的標點

/**
 * 依寬度斷行
 * 英數字連續字串視為一個單字，盡量不從單字中間斷開；單字本身超過整行時才逐字拆開
 * 避頭點：標點若會落在行首，連同前一個字一起換到下一行
 * @param {CanvasRenderingContext2D} ctx 已設定好字型的 context
 * @param {string} text 文字
 * @param {number} maxWidth 每行最大寬度
 * @returns {string[]}
 */
const wrapText = (ctx, text, maxWidth) => {
  const lines = []
  let line = ''
  const pushLine = () => {
    if (line.trim()) lines.push(line.trimEnd())
    line = ''
  }
  const tokens = String(text).match(/[\x21-\x7e]+|\s+|[^\s\x21-\x7e]/gu) || []
  tokens.forEach((token) => {
    if (/^\s+$/.test(token)) {
      if (line) line += ' '
      return
    }
    if (ctx.measureText(line + token).width <= maxWidth) {
      line += token
      return
    }
    let carry = '' // 避頭點時帶到下一行的字
    const lineChars = Array.from(line)
    if (CLOSING_PUNCTUATION.includes(token) && lineChars.length > 1) {
      carry = lineChars.pop()
      line = lineChars.join('')
    }
    pushLine()
    line = carry
    Array.from(token).forEach((char) => {
      if (line && ctx.measureText(line + char).width > maxWidth) pushLine()
      line += char
    })
  })
  pushLine()
  return lines
}

/**
 * 點數表列資料（規則同模板 V3）
 * @returns {Array<[string, number, number]>} [點數類型, 交易前, 交易後]
 */
const getPointRows = (data) => {
  if (data.isPointSupplement) {
    return [
      ['一般點數', data.normalPointBefore, data.normalPointAfter],
      ['現金點數', data.cashPointBefore, data.cashPointAfter]
    ]
  }
  return data.selectedPointType === 1
    ? [['一般點數', data.normalPointBefore, data.normalPointAfter]]
    : [['現金點數', data.cashPointBefore, data.cashPointAfter]]
}

/**
 * 繪製明細單內容
 * 字級、行高、欄寬皆以 fontScale = 1、textScaleY = 1 時的數值撰寫，再依樣式參數換算
 * @param {CanvasRenderingContext2D} ctx
 * @param {object} data 列印資料
 * @param {HTMLCanvasElement | null} logo Logo 剪影
 * @param {object} style 樣式參數，見 DEFAULT_RECEIPT_STYLE
 * @returns {number} 內容總高度
 */
const paintReceipt = (ctx, data, logo, style) => {
  const W = RECEIPT_CANVAS_WIDTH
  const { fontScale, textScaleY } = style
  const scaleX = (value) => Math.round(value * fontScale) // 字級與水平尺寸
  const lineHeight = (fontSize, ratio) => Math.round(scaleX(fontSize) * ratio * textScaleY) // 行高 = 字級 × 行距比例 × 垂直拉長
  const barHeight = Math.round(scaleX(22) * textScaleY) + BAR_PADDING * 2 // 黑底標題列高度
  const setFont = (size, bold = false) => { ctx.font = `${bold ? 'bold ' : ''}${scaleX(size)}px ${FONT_FAMILY}` }
  // 文字只做垂直拉長，寬度不變（量測寬度的 measureText 不受影響）
  const drawText = (text, x, y, align = 'left', color = '#000') => {
    ctx.save()
    ctx.fillStyle = color
    ctx.textAlign = align
    ctx.translate(x, y)
    ctx.scale(1, textScaleY)
    ctx.fillText(text, 0, 0)
    ctx.restore()
  }
  const fillBlack = (x, y, width, height) => {
    ctx.fillStyle = '#000'
    ctx.fillRect(x, y, width, height)
  }

  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, W, ctx.canvas.height)
  ctx.textBaseline = 'top'
  let y = 4

  // Logo 置中
  if (logo) {
    ctx.drawImage(logo, Math.round((W - logo.width) / 2), y)
    y += logo.height + 14
  }

  // 標題
  setFont(26)
  drawText(`明細單${data.isVoidOrCancel ? ' (拒單/作廢/取消)' : ''}`, W / 2, y, 'center')
  y += lineHeight(26, 1.46)
  fillBlack(PADDING, y, W - PADDING * 2, 2)
  y += 14

  // 訂單編號、用餐方式、戶號
  setFont(24)
  ;[`訂單：${data.comOrderId}`, data.dining, `戶號：${data.userId}`].forEach((text) => {
    if (!text) return
    drawText(text, PADDING, y)
    y += lineHeight(24, 1.42)
  })
  y += 8

  // 品項欄位：| 品名／規格 | 數量方塊 | 金額（靠右）|
  const qtyBoxWidth = scaleX(QTY_BOX_WIDTH)
  const qtyBoxX = W - PADDING - scaleX(AMOUNT_WIDTH) - qtyBoxWidth
  const nameMaxWidth = qtyBoxX - 10 - PADDING

  // 品項標題列（黑底白字）
  fillBlack(0, y, W, barHeight)
  setFont(22, true)
  drawText('品項 / 規格', PADDING + 6, y + BAR_PADDING, 'left', '#fff')
  drawText('數量', qtyBoxX + qtyBoxWidth / 2, y + BAR_PADDING, 'center', '#fff')
  drawText('金額', W - PADDING - 2, y + BAR_PADDING, 'right', '#fff')
  y += barHeight + 12

  // 品項列表
  data.items.forEach((item) => {
    const blockTop = y - 2
    setFont(32, true)
    wrapText(ctx, item.name || '', nameMaxWidth).forEach((line) => {
      drawText(line, PADDING, y)
      y += lineHeight(32, 1.25)
    })
    if (item.displaySpec) {
      setFont(22)
      const prefix = `${SPEC_BULLET} `
      const indent = ctx.measureText(prefix).width // 規格換行時懸掛縮排，對齊符號後的文字
      wrapText(ctx, item.displaySpec, nameMaxWidth - indent).forEach((line, index) => {
        if (index === 0) drawText(prefix, PADDING + 2, y)
        drawText(line, PADDING + 2 + indent, y)
        y += lineHeight(22, 1.36)
      })
    }
    y += 4
    const blockBottom = y

    // 數量黑底方塊涵蓋整個品項區塊，數量與金額垂直置中
    const quantityText = String(item.quantity)
    const middleY = (blockTop + blockBottom) / 2
    fillBlack(qtyBoxX, blockTop, qtyBoxWidth, blockBottom - blockTop)
    ctx.textBaseline = 'middle'
    setFont(quantityText.length > 2 ? 20 : 26, true) // 三位數以上縮小，避免超出方塊
    drawText(quantityText, qtyBoxX + qtyBoxWidth / 2, middleY, 'center', '#fff')
    setFont(24)
    drawText(String(item.quantity * item.price), W - PADDING, middleY, 'right')
    ctx.textBaseline = 'top'

    y += 10
    fillBlack(PADDING, y, W - PADDING * 2, 1)
    y += 11
  })

  // 合計
  y += 4
  setFont(30, true)
  drawText(`合計：${data.totalPoint}`, W - PADDING, y, 'right')
  y += lineHeight(30, 1.67)

  // 點數表標題列（黑底白字）
  const afterX = W - PADDING - 2
  const beforeX = afterX - scaleX(POINT_COLUMN_WIDTH)
  fillBlack(0, y, W, barHeight)
  setFont(22, true)
  drawText('點數類型', PADDING + 6, y + BAR_PADDING, 'left', '#fff')
  drawText('交易前', beforeX, y + BAR_PADDING, 'right', '#fff')
  drawText('交易後', afterX, y + BAR_PADDING, 'right', '#fff')
  y += barHeight + 10
  getPointRows(data).forEach(([pointType, before, after]) => {
    setFont(24, true)
    drawText(pointType, PADDING + 6, y)
    setFont(24)
    drawText(String(before), beforeX, y, 'right')
    drawText(String(after), afterX, y, 'right')
    y += lineHeight(24, 1.42)
  })
  y += 10
  fillBlack(PADDING, y, W - PADDING * 2, 2)
  y += 16

  // 備註、時間、簽名
  setFont(24)
  const noteLabel = data.isVoidOrCancel ? '拒單 / 作廢 / 取消備註：' : '備註：'
  wrapText(ctx, `${noteLabel}${data.note || ''}`, W - PADDING * 2).forEach((line) => {
    drawText(line, PADDING, y)
    y += lineHeight(24, 1.42)
  })
  y += 6
  drawText(`時間：${data.date}`, PADDING, y)
  y += lineHeight(24, 1.5) + SIGNATURE_GAP // 預留簽名空間
  drawText(`簽名：${'_'.repeat(20)}`, PADDING, y)
  y += lineHeight(24, 1.5)
  return y + 10
}

/** 依門檻值轉成純黑白，預覽看到的即為實際送印的點陣 */
const toMonochrome = (canvas) => {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const d = imageData.data
  for (let i = 0; i < d.length; i += 4) {
    const luminance = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
    const value = luminance < BW_THRESHOLD ? 0 : 255
    d[i] = d[i + 1] = d[i + 2] = value
    d[i + 3] = 255
  }
  ctx.putImageData(imageData, 0, 0)
}

/**
 * 畫出明細單（模板 V4）
 * @param {object} data 列印資料，欄位同模板 V3（comOrderId、dining、userId、items、點數、note、date、isVoidOrCancel）
 * @param {object} style 樣式參數，未指定的欄位使用 DEFAULT_RECEIPT_STYLE（正式流程不傳，僅後門頁試調時使用）
 * @returns {Promise<HTMLCanvasElement>} 純黑白的明細單畫布，寬度為 RECEIPT_CANVAS_WIDTH
 */
export const drawReceiptCanvas = async (data, style = {}) => {
  const receiptStyle = { ...DEFAULT_RECEIPT_STYLE, ...style }
  const logoImage = await loadLogoImage().catch(() => null) // Logo 載入失敗時仍照常出單
  const logo = logoImage ? createLogoSilhouette(logoImage) : null
  // 先在 1px 高的畫布上排版取得總高度（超出畫布的繪製不會生效），再以實際高度正式繪製
  const measureCanvas = createCanvas(RECEIPT_CANVAS_WIDTH, 1)
  const height = Math.ceil(paintReceipt(measureCanvas.getContext('2d'), data, logo, receiptStyle))
  const canvas = createCanvas(RECEIPT_CANVAS_WIDTH, height)
  paintReceipt(canvas.getContext('2d', { willReadFrequently: true }), data, logo, receiptStyle)
  toMonochrome(canvas)
  return canvas
}
